import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import { SUBTASK_STATUS_META, SUBTASK_STATUS_ORDER, normSub, subtaskStats } from '@/constants/projectMeta';

const ProgressInput = ({ sub, disabled, onCommit }) => {
  const [value, setValue] = useState(String(sub.progress));
  useEffect(() => { setValue(String(sub.progress)); }, [sub.progress]);
  const commit = () => {
    const n = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    setValue(String(n));
    if (n !== sub.progress) onCommit(n);
  };
  return (
    <div className="flex items-center gap-1">
      <Input type="number" min={0} max={100} value={value} disabled={disabled}
        onChange={(e) => setValue(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
        className="h-7 w-16 text-xs" data-testid={`subtask-progress-${sub.id}`} />
      <span className="text-xs text-[hsl(var(--text-muted))]">%</span>
    </div>
  );
};

const SubtaskRow = ({ sub, members, onPatch, onAssign, onDelete }) => {
  const s = normSub(sub);
  const meta = SUBTASK_STATUS_META[s.status];
  const cancelled = s.status === 'cancelled';
  const struck = cancelled || s.status === 'done';
  const overdue = !!s.deadline && !struck && s.deadline < new Date(Date.now() + 8 * 3600000).toISOString().slice(0, 10);
  return (
    <div className={`rounded-lg border bg-[hsl(var(--surface))] px-2.5 py-2 transition-opacity ${cancelled ? 'border-dashed border-[hsl(var(--border))] opacity-60' : 'border-[hsl(var(--border))]'}`}
      data-testid={`subtask-entry-${s.id}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={s.status === 'done'} disabled={cancelled}
          onChange={() => onPatch(sub, { done: s.status !== 'done' })}
          className="h-3.5 w-3.5 accent-[hsl(var(--primary))] cursor-pointer disabled:cursor-not-allowed" data-testid={`subtask-check-${s.id}`} />
        <span className={`flex-1 text-sm truncate ${struck ? 'line-through text-[hsl(var(--text-muted))]' : 'text-[hsl(var(--text-primary))]'}`}
          title={s.title} data-testid={`subtask-title-${s.id}`}>
          {s.title}
        </span>
        <button type="button" onClick={() => onDelete(sub)} className="text-[hsl(var(--text-muted))] hover:text-red-400" data-testid={`subtask-delete-${s.id}`}>
          <Trash2 size={12} />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2 pl-5">
        <Select value={s.status} onValueChange={(v) => onPatch(sub, { status: v })}>
          <SelectTrigger className={`h-7 w-[7.5rem] text-xs ${meta.bg} ${meta.text} ${meta.border}`} data-testid={`subtask-status-${s.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUBTASK_STATUS_ORDER.map((k) => (
              <SelectItem key={k} value={k} data-testid={`subtask-status-option-${k}`}>{SUBTASK_STATUS_META[k].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ProgressInput sub={s} disabled={cancelled} onCommit={(n) => onPatch(sub, { progress: n })} />
        <div className="flex-1 min-w-[48px] h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid={`subtask-bar-${s.id}`}>
          <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${s.progress}%`, background: meta.accent }} />
        </div>
        <Input type="date" value={s.deadline || ''} disabled={cancelled}
          onChange={(e) => onPatch(sub, { deadline: e.target.value })}
          title={overdue ? 'Overdue' : 'Subtask deadline'}
          className={`h-7 w-[8.5rem] text-xs shrink-0 ${overdue ? 'border-red-500/60 text-red-400' : ''}`}
          data-testid={`subtask-deadline-${s.id}`} />
        <Select value={s.assignee_id || 'none'} onValueChange={(v) => onAssign(sub, v)}>
          <SelectTrigger className="h-7 w-[8.5rem] text-xs shrink-0" data-testid={`subtask-pic-${s.id}`}>
            <SelectValue placeholder="Assign PIC" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No PIC</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`subtask-pic-option-${m.id}`}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const SubtaskList = ({ subtasks = [], members = [], onAdd, onPatch, onAssign, onDelete }) => {
  const [title, setTitle] = useState('');
  const st = subtaskStats(subtasks);
  const add = async () => {
    const t = title.trim();
    if (!t) return;
    await onAdd(t);
    setTitle('');
  };
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">Subtasks</p>
        {!!subtasks.length && (
          <span className="text-[11px] text-[hsl(var(--text-muted))]" data-testid="subtask-progress">
            {st.done}/{st.active} done{st.cancelled ? ` · ${st.cancelled} cancelled` : ''}{st.avg !== null ? ` · avg ${st.avg}%` : ''}
          </span>
        )}
      </div>
      <div className="space-y-1.5" data-testid="subtask-list">
        {!subtasks.length && <p className="text-xs text-[hsl(var(--text-muted))]">No subtasks yet.</p>}
        {subtasks.map((s) => (
          <SubtaskRow key={s.id} sub={s} members={members} onPatch={onPatch} onAssign={onAssign} onDelete={onDelete} />
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add a subtask and press Enter" data-testid="subtask-input" />
        <Button type="button" variant="outline" onClick={add} disabled={!title.trim()} data-testid="subtask-add-btn">
          <Plus size={14} />
        </Button>
      </div>
    </div>
  );
};
